import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TablePagination,
  TextField,
  Typography
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import ReplayIcon from "@material-ui/icons/Replay";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import AddIcon from "@material-ui/icons/Add";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import HistoryIcon from "@material-ui/icons/History";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { filterValidContacts } from "../../utils/contactValidation";

const RESPONSIBLE_ACCENT_COLORS = [
  "#0ea5e9",
  "#22c55e",
  "#f97316",
  "#a855f7",
  "#ef4444",
  "#14b8a6",
  "#eab308",
  "#6366f1"
];

const useStyles = makeStyles(theme => ({
  hero: {
    borderRadius: 18,
    padding: theme.spacing(1.4, 1.8),
    marginBottom: theme.spacing(1.1),
    background: `linear-gradient(135deg, ${alpha("#0f2744", 0.96)} 0%, ${alpha("#1498d5", 0.82)} 100%)`,
    color: "#fff",
    boxShadow: `0 14px 30px ${alpha("#0f2744", 0.16)}`,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 1.15),
      marginBottom: theme.spacing(0.85)
    }
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    background: alpha("#ffffff", 0.12),
    marginBottom: theme.spacing(0.65),
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontSize: 10,
    fontWeight: 700
  },
  heroGrid: {
    display: "grid",
    gridTemplateColumns: "1.4fr 0.9fr",
    gap: theme.spacing(1.15),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  heroTitle: {
    fontWeight: 800,
    marginBottom: 4,
    fontSize: "1.95rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.35rem"
    }
  },
  heroKpiCard: {
    borderRadius: 14,
    border: `1px solid ${alpha("#ffffff", 0.14)}`,
    background: alpha("#ffffff", 0.08),
    padding: theme.spacing(1, 1.15)
  },
  statCard: {
    height: "100%",
    borderRadius: 16,
    padding: theme.spacing(1, 1.15),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: `0 14px 28px ${alpha("#0f2744", 0.07)}`,
    background: theme.palette.type === "dark"
      ? `linear-gradient(180deg, ${alpha("#1f334a", 0.95)} 0%, ${alpha("#172434", 0.95)} 100%)`
      : `linear-gradient(180deg, #ffffff 0%, #f5fbff 100%)`
  },
  statCaption: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    opacity: 0.75,
    marginBottom: theme.spacing(0.75)
  },
  filtersCard: {
    borderRadius: 16,
    padding: theme.spacing(1.15),
    marginBottom: theme.spacing(1.3),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
  },
  tasksBoard: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    boxShadow: `0 12px 28px ${alpha("#0f2744", 0.07)}`
  },
  boardHead: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 2.1fr) minmax(130px, 0.9fr) minmax(160px, 1fr) minmax(210px, 1.15fr) minmax(240px, 1.35fr)",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.4),
    background: theme.palette.type === "dark" ? alpha("#0f172a", 0.6) : alpha("#0f2744", 0.04),
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    [theme.breakpoints.down("md")]: {
      display: "none"
    }
  },
  boardHeadCell: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    color: theme.palette.text.secondary
  },
  taskRow: {
    position: "relative",
    padding: theme.spacing(1.15, 1.4),
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    overflow: "hidden",
    background: theme.palette.background.paper,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  taskAccent: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 5
  },
  taskRowTop: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 2.1fr) minmax(130px, 0.9fr) minmax(160px, 1fr) minmax(210px, 1.15fr) minmax(240px, 1.35fr)",
    gap: theme.spacing(1),
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    }
  },
  timerCard: {
    borderRadius: 14,
    padding: theme.spacing(0.9, 1),
    textAlign: "center",
    background: theme.palette.type === "dark"
      ? alpha("#0f172a", 0.72)
      : alpha("#0f2744", 0.04),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
  },
  timerValue: {
    fontFamily: "Consolas, 'Courier New', monospace",
    fontWeight: 800,
    fontSize: "1.05rem",
    letterSpacing: "0.06em"
  },
  infoColumn: {
    minWidth: 0
  },
  taskMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: theme.spacing(0.75)
  },
  dataColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.45)
  },
  dataLabel: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary
  },
  actionsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    justifyContent: "flex-end",
    [theme.breakpoints.down("md")]: {
      justifyContent: "flex-start"
    }
  },
  emptyState: {
    padding: theme.spacing(4),
    borderRadius: 18,
    textAlign: "center",
    border: `1px dashed ${alpha(theme.palette.primary.main, 0.18)}`
  },
  historyItem: {
    borderRadius: 14,
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
  },
  liveBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    background: alpha("#22c55e", 0.14),
    color: "#15803d"
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "currentColor"
  },
  paginationWrap: {
    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
  }
}));

const PRIORITY_OPTIONS = [
  { value: "urgent_important", label: "Urgentes e importantes (inmediato)", color: "#ef4444" },
  { value: "urgent_not_important", label: "Urgentes pero no importantes (delegables)", color: "#f97316" },
  { value: "important_not_urgent", label: "Importantes pero no urgentes (planificables)", color: "#0ea5e9" },
  { value: "not_urgent_not_important", label: "No urgentes ni importantes (aplazables)", color: "#64748b" }
];

const STATUS_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "pending", label: "Pendientes" },
  { value: "in_progress", label: "En curso" },
  { value: "paused", label: "Pausadas" },
  { value: "completed", label: "Terminadas" }
];

const PAUSE_REASON_OPTIONS = [
  { value: "bathroom", label: "Fue al baño" },
  { value: "lunch", label: "Se fue a almorzar" },
  { value: "customer_waiting", label: "Quedó esperando respuesta del cliente" },
  { value: "customer_left", label: "El cliente se retiró o no siguió" },
  { value: "pending_external", label: "Quedó pendiente de tercero o trámite" },
  { value: "internal_support", label: "Necesita apoyo interno" },
  { value: "other", label: "Otro motivo" }
];

const REOPEN_REASON_OPTIONS = [
  { value: "customer_followup", label: "El cliente volvió a solicitar seguimiento" },
  { value: "correction_needed", label: "Se requiere corrección" },
  { value: "missing_delivery", label: "Quedó algo pendiente por entregar" },
  { value: "new_requirement", label: "Apareció un requerimiento adicional" },
  { value: "other", label: "Otro motivo" }
];

const emptyForm = {
  title: "",
  description: "",
  priority: "important_not_urgent",
  assignedUserId: "",
  contactId: "",
  businessCustomerId: "",
  customerEstablishmentId: ""
};

const emptyActionForm = {
  reason: "",
  note: ""
};

const TASK_WIDGET_SYNC_EVENT = "operational-task-widget-sync";

const formatDuration = totalSeconds => {
  const safe = Math.max(0, Number(totalSeconds || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const formatDateTime = value => {
  if (!value) return "Sin fecha";
  return new Date(value).toLocaleString("es-CO");
};

const getPriorityMeta = value =>
  PRIORITY_OPTIONS.find(option => option.value === value) || PRIORITY_OPTIONS[2];

const getStatusLabel = value =>
  ({
    pending: "Pendiente",
    in_progress: "En curso",
    paused: "Pausada",
    completed: "Terminada"
  }[value] || value);

const getEventLabel = value =>
  ({
    created: "Creada",
    updated: "Editada",
    started: "Iniciada",
    restarted: "Vuelta a iniciar",
    paused: "Pausada",
    resumed: "Retomada",
    completed: "Terminada",
    reopened: "Reabierta"
  }[value] || value);

const isNumericOnly = value => /^\d+$/.test(String(value || "").trim());

const hasUsableContactName = contact => {
  const name = String(contact?.name || "").trim();
  const number = String(contact?.number || "").trim();

  if (!name) return false;
  if (!isNumericOnly(name)) return true;
  if (!number) return false;

  return name !== number && name.length < 12;
};

const buildTaskContactLabel = contact => {
  if (!contact) return "";

  const rawName = String(contact.name || "").trim();
  const rawNumber = String(contact.number || "").trim();
  const name = hasUsableContactName(contact) ? rawName : "";

  if (name && rawNumber) {
    return `${name} - ${rawNumber}`;
  }

  if (name) {
    return name;
  }

  if (rawNumber) {
    return rawNumber;
  }

  return `Contacto #${contact.id}`;
};

const stringToColor = value => {
  const source = String(value || "default");
  let sum = 0;
  for (let index = 0; index < source.length; index += 1) {
    sum += source.charCodeAt(index);
  }
  return RESPONSIBLE_ACCENT_COLORS[sum % RESPONSIBLE_ACCENT_COLORS.length];
};

export default function ToDoList() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filters, setFilters] = useState({
    q: "",
    status: "all",
    assignedUserId: ""
  });
  const [historyDialog, setHistoryDialog] = useState({ open: false, task: null, events: [] });
  const [pauseDialog, setPauseDialog] = useState({ open: false, task: null });
  const [reopenDialog, setReopenDialog] = useState({ open: false, task: null });
  const [actionForm, setActionForm] = useState(emptyActionForm);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  const canSeeAll = String(user?.profile) === "admin" || String(user?.profile) === "super";

  const loadClients = useCallback(async () => {
    try {
      const { data } = await api.get("/clients/list");
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const loadEstablishments = useCallback(async clientId => {
    if (!clientId) {
      setEstablishments([]);
      return;
    }

    try {
      const { data } = await api.get(`/clients/${clientId}/establishments/list`);
      setEstablishments(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
      setEstablishments([]);
    }
  }, []);

  const loadData = useCallback(async (options = {}) => {
    try {
      if (!options.silent) {
        setLoading(true);
      }
      const params = { includeCompleted: "true" };
      if (filters.q) params.q = filters.q;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.assignedUserId) params.assignedUserId = filters.assignedUserId;

      const requests = [
        api.get("/operational-tasks", { params }),
        api.get("/contacts/list")
      ];
      if (canSeeAll) {
        requests.splice(1, 0, api.get("/users"));
      }

      const responses = await Promise.all(requests);
      const tasksResponse = responses[0];
      const usersResponse = canSeeAll ? responses[1] : null;
      const contactsResponse = canSeeAll ? responses[2] : responses[1];

      setRecords(Array.isArray(tasksResponse.data) ? tasksResponse.data : []);
      setUsers(canSeeAll && Array.isArray(usersResponse?.data?.users) ? usersResponse.data.users : []);
      setContacts(Array.isArray(contactsResponse?.data) ? contactsResponse.data : []);
    } catch (err) {
      toastError(err);
    } finally {
      if (!options.silent) {
        setLoading(false);
      }
    }
  }, [canSeeAll, filters.assignedUserId, filters.q, filters.status]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    const interval = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      loadData({ silent: true });
    }, 15000);

    return () => clearInterval(interval);
  }, [loadData]);

  const enrichedRecords = useMemo(
    () =>
      records.map(record => {
        const liveSeconds = record.status === "in_progress" && record.lastResumedAt
          ? Math.max(0, Math.floor((nowTick - new Date(record.lastResumedAt).getTime()) / 1000))
          : 0;
        return {
          ...record,
          elapsedSecondsLive: Number(record.accumulatedSeconds || 0) + liveSeconds,
          responsibleColor: stringToColor(
            record.assignedUser?.email || record.assignedUser?.name || record.assignedUserId || record.id
          )
        };
      }),
    [nowTick, records]
  );

  const stats = useMemo(() => {
    const active = enrichedRecords.filter(item => item.status === "in_progress").length;
    const paused = enrichedRecords.filter(item => item.status === "paused").length;
    const pending = enrichedRecords.filter(item => item.status === "pending").length;
    const completed = enrichedRecords.filter(item => item.status === "completed").length;
    const myTasks = enrichedRecords.filter(item => Number(item.assignedUserId) === Number(user?.id)).length;
    return { active, paused, pending, completed, myTasks };
  }, [enrichedRecords, user?.id]);

  const taskContactOptions = useMemo(() => {
    const filtered = filterValidContacts(contacts)
      .filter(contact => !contact.isGroup)
      .filter(contact => {
        const number = String(contact.number || "").trim();
        const name = String(contact.name || "").trim();

        if (hasUsableContactName(contact)) return true;
        if (!number) return false;
        if (!isNumericOnly(number)) return true;

        return number.length <= 15 && name !== number;
      })
      .sort((left, right) => {
        const leftHasName = hasUsableContactName(left);
        const rightHasName = hasUsableContactName(right);

        if (leftHasName !== rightHasName) {
          return leftHasName ? -1 : 1;
        }

        return buildTaskContactLabel(left).localeCompare(buildTaskContactLabel(right), "es", {
          sensitivity: "base",
          numeric: true
        });
      });

    const seen = new Set();
    return filtered.filter(contact => {
      const key = `${String(contact.name || "").trim()}|${String(contact.number || "").trim()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [contacts]);

  const selectedTaskContact = useMemo(
    () => taskContactOptions.find(option => String(option.id) === String(form.contactId)) || null,
    [form.contactId, taskContactOptions]
  );

  const clientOptions = useMemo(
    () =>
      [...clients].sort((left, right) =>
        String(left.name || "").localeCompare(String(right.name || ""), "es", {
          sensitivity: "base",
          numeric: true
        })
      ),
    [clients]
  );

  const selectedClient = useMemo(
    () => clientOptions.find(option => String(option.id) === String(form.businessCustomerId)) || null,
    [clientOptions, form.businessCustomerId]
  );

  const selectedEstablishment = useMemo(
    () => establishments.find(option => String(option.id) === String(form.customerEstablishmentId)) || null,
    [establishments, form.customerEstablishmentId]
  );

  useEffect(() => {
    if (!form.businessCustomerId) {
      setEstablishments([]);
      return;
    }

    loadEstablishments(form.businessCustomerId);
  }, [form.businessCustomerId, loadEstablishments]);

  const openCreateDialog = () => {
    setEditingTask(null);
    setForm({
      ...emptyForm,
      assignedUserId: String(user?.id || "")
    });
    setEstablishments([]);
    setDialogOpen(true);
  };

  const openEditDialog = task => {
    setEditingTask(task);
    setForm({
      title: task.title || "",
      description: task.description || "",
      priority: task.priority || "important_not_urgent",
      assignedUserId: String(task.assignedUserId || user?.id || ""),
      contactId: task.contactId ? String(task.contactId) : "",
      businessCustomerId: task.businessCustomerId ? String(task.businessCustomerId) : "",
      customerEstablishmentId: task.customerEstablishmentId ? String(task.customerEstablishmentId) : ""
    });
    if (task.businessCustomerId) {
      loadEstablishments(task.businessCustomerId);
    } else {
      setEstablishments([]);
    }
    setDialogOpen(true);
  };

  const openDuplicateDialog = task => {
    setEditingTask(null);
    setForm({
      title: `${task.title || "Tarea"} (copia)`,
      description: task.description || "",
      priority: task.priority || "important_not_urgent",
      assignedUserId: String(task.assignedUserId || user?.id || ""),
      contactId: task.contactId ? String(task.contactId) : "",
      businessCustomerId: task.businessCustomerId ? String(task.businessCustomerId) : "",
      customerEstablishmentId: task.customerEstablishmentId ? String(task.customerEstablishmentId) : ""
    });
    if (task.businessCustomerId) {
      loadEstablishments(task.businessCustomerId);
    } else {
      setEstablishments([]);
    }
    setDialogOpen(true);
  };

  const openPauseDialog = task => {
    setPauseDialog({ open: true, task });
    setActionForm(emptyActionForm);
  };

  const openReopenDialog = task => {
    setReopenDialog({ open: true, task });
    setActionForm(emptyActionForm);
  };

  const handleSave = async () => {
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        assignedUserId: canSeeAll ? Number(form.assignedUserId || user?.id) : Number(user?.id),
        contactId: form.contactId ? Number(form.contactId) : null,
        businessCustomerId: form.businessCustomerId ? Number(form.businessCustomerId) : null,
        customerEstablishmentId: form.customerEstablishmentId ? Number(form.customerEstablishmentId) : null
      };

      if (!payload.title) {
        toast.warning("Escriba un nombre para la tarea.");
        return;
      }

      if (editingTask) {
        await api.put(`/operational-tasks/${editingTask.id}`, payload);
        toast.success("Tarea actualizada");
      } else {
        await api.post("/operational-tasks", payload);
        toast.success("Tarea creada");
      }

      window.dispatchEvent(new Event(TASK_WIDGET_SYNC_EVENT));

      setDialogOpen(false);
      setEditingTask(null);
      setForm(emptyForm);
      setEstablishments([]);
      loadData();
    } catch (err) {
      toastError(err);
    }
  };

  const handleTransition = async (task, action, extraPayload = {}) => {
    try {
      await api.post(`/operational-tasks/${task.id}/${action}`, extraPayload);
      window.dispatchEvent(new Event(TASK_WIDGET_SYNC_EVENT));
      loadData();
    } catch (err) {
      toastError(err);
    }
  };

  const loadHistory = async task => {
    try {
      const { data } = await api.get(`/operational-tasks/${task.id}/history`);
      setHistoryDialog({
        open: true,
        task: data?.task || task,
        events: Array.isArray(data?.events) ? data.events : []
      });
    } catch (err) {
      toastError(err);
    }
  };

  const tasksToRender = useMemo(() => {
    const order = { in_progress: 0, pending: 1, paused: 2, completed: 3 };
    return [...enrichedRecords].sort((a, b) => {
      const orderDiff = (order[a.status] ?? 99) - (order[b.status] ?? 99);
      if (orderDiff !== 0) return orderDiff;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [enrichedRecords]);

  const pagedTasks = useMemo(() => {
    const start = page * rowsPerPage;
    return tasksToRender.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, tasksToRender]);

  return (
    <MainContainer maxWidth={false}>
      <Paper className={classes.hero} elevation={0}>
        <div className={classes.heroBadge}>
          <AccessTimeIcon fontSize="small" />
          Panel operativo de tareas
        </div>
        <div className={classes.heroGrid}>
          <div>
            <Typography variant="h4" className={classes.heroTitle}>
              Tareas del equipo
            </Typography>
            <Typography variant="body2" style={{ opacity: 0.92, maxWidth: 720, lineHeight: 1.45 }}>
              Este módulo mide trabajo operativo distinto al ticket de WhatsApp. Sirve para ventas presenciales, visitas, gestiones internas o cualquier tarea que quiera medir de principio a fin.
            </Typography>
          </div>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <div className={classes.heroKpiCard}>
                <Typography variant="caption" style={{ opacity: 0.82, textTransform: "uppercase" }}>En curso</Typography>
                <Typography variant="h5" style={{ fontWeight: 800 }}>{stats.active}</Typography>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.heroKpiCard}>
                <Typography variant="caption" style={{ opacity: 0.82, textTransform: "uppercase" }}>Pendientes</Typography>
                <Typography variant="h5" style={{ fontWeight: 800 }}>{stats.pending}</Typography>
              </div>
            </Grid>
          </Grid>
        </div>
      </Paper>

      <MainHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Title>Tareas operativas ({records.length})</Title>
            <Typography variant="body2" color="textSecondary">
              {canSeeAll
                ? "Vista gerencial en tiempo real. Puede ver las tareas de todo el equipo, filtrarlas por responsable y revisar su movimiento."
                : "Vista operativa personal. Aqui solo ve sus tareas y cada pausa, reapertura y cierre queda guardado en historial."}
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Button fullWidth variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
                  Recargar
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button fullWidth variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreateDialog}>
                  Nueva tarea
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>

      <Grid container spacing={1} style={{ marginBottom: 8 }}>
        <Grid item xs={12} md={2}>
          <Paper className={classes.statCard}>
            <Typography className={classes.statCaption}>Mis tareas</Typography>
            <Typography variant="h4" style={{ fontWeight: 800 }}>{stats.myTasks}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={2}>
          <Paper className={classes.statCard}>
            <Typography className={classes.statCaption}>Pendientes</Typography>
            <Typography variant="h4" style={{ fontWeight: 800 }}>{stats.pending}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={2}>
          <Paper className={classes.statCard}>
            <Typography className={classes.statCaption}>En curso</Typography>
            <Typography variant="h4" style={{ fontWeight: 800 }}>{stats.active}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={2}>
          <Paper className={classes.statCard}>
            <Typography className={classes.statCaption}>Pausadas</Typography>
            <Typography variant="h4" style={{ fontWeight: 800 }}>{stats.paused}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={2}>
          <Paper className={classes.statCard}>
            <Typography className={classes.statCaption}>Terminadas</Typography>
            <Typography variant="h4" style={{ fontWeight: 800 }}>{stats.completed}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={2}>
          <Paper className={classes.statCard}>
            <Typography className={classes.statCaption}>Vista</Typography>
            <Typography variant="h6" style={{ fontWeight: 800 }}>{canSeeAll ? "Gerencial" : "Operativa"}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper className={classes.filtersCard} elevation={0}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField fullWidth variant="outlined" label="Buscar tarea" value={filters.q} onChange={event => { setFilters(prev => ({ ...prev, q: event.target.value })); setPage(0); }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth variant="outlined" label="Estado" value={filters.status} onChange={event => { setFilters(prev => ({ ...prev, status: event.target.value })); setPage(0); }}>
              {STATUS_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth variant="outlined" label="Responsable" value={filters.assignedUserId} onChange={event => { setFilters(prev => ({ ...prev, assignedUserId: event.target.value })); setPage(0); }}>
              <MenuItem value="">Todos</MenuItem>
              {(canSeeAll ? users : [user]).map(option => (
                <MenuItem key={option.id} value={String(option.id)}>{option.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={() => { setFilters({ q: "", status: "all", assignedUserId: "" }); setPage(0); }}>
              Limpiar
            </Button>
          </Grid>
          {canSeeAll && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                La barra lateral de cada tarjeta identifica visualmente al responsable para seguimiento rapido desde gerencia.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {tasksToRender.length === 0 ? (
        <Paper className={classes.emptyState} elevation={0}>
          <Typography variant="h6" gutterBottom>No hay tareas para mostrar</Typography>
          <Typography variant="body2" color="textSecondary">
            Cree la primera tarea operativa para empezar a medir tiempos y seguimiento.
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper elevation={0} className={classes.tasksBoard}>
            <div className={classes.boardHead}>
              <div className={classes.boardHeadCell}>Tarea</div>
              <div className={classes.boardHeadCell}>Tiempo</div>
              <div className={classes.boardHeadCell}>Movimiento</div>
              <div className={classes.boardHeadCell}>Responsable / Contacto</div>
              <div className={classes.boardHeadCell}>Acciones</div>
            </div>
          {pagedTasks.map(task => {
            const priority = getPriorityMeta(task.priority);
            return (
              <div key={task.id} className={classes.taskRow}>
                <div className={classes.taskAccent} style={{ backgroundColor: task.responsibleColor }} />
                <div className={classes.taskRowTop}>
                  <div className={classes.infoColumn}>
                    <Typography variant="subtitle1" style={{ fontWeight: 800, marginBottom: 2 }}>
                      {task.title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Creada: {formatDateTime(task.createdAt)}
                    </Typography>
                    {task.description && (
                      <Typography variant="body2" style={{ marginTop: 6, lineHeight: 1.4 }}>
                        {task.description}
                      </Typography>
                    )}
                    <div className={classes.taskMeta}>
                      <Chip size="small" label={priority.label} style={{ backgroundColor: alpha(priority.color, 0.12), color: priority.color, fontWeight: 700 }} />
                      <Chip size="small" label={getStatusLabel(task.status)} />
                    {canSeeAll && task.assignedUser?.name && (
                      <Chip
                        size="small"
                        label={task.assignedUser.name}
                        style={{
                          backgroundColor: alpha(task.responsibleColor, 0.12),
                          color: task.responsibleColor,
                          fontWeight: 700
                        }}
                      />
                    )}
                    </div>
                  </div>

                  <div className={classes.timerCard}>
                    <Typography variant="caption" color="textSecondary">Tiempo real acumulado</Typography>
                    <div className={classes.timerValue}>{formatDuration(task.elapsedSecondsLive)}</div>
                    <Typography variant="caption" color="textSecondary">
                      {task.status === "in_progress" ? "Corriendo ahora" : "Último acumulado"}
                    </Typography>
                    {task.status === "in_progress" && (
                      <div className={classes.liveBadge} style={{ marginTop: 10 }}>
                        <span className={classes.dot} />
                        En tiempo real
                      </div>
                    )}
                  </div>

                  <div className={classes.dataColumn}>
                    <Typography className={classes.dataLabel}>Movimiento</Typography>
                    <Typography variant="body2">
                      Inicio: {task.startedAt ? formatDateTime(task.startedAt) : "Aún no inicia"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Último cambio: {formatDateTime(task.updatedAt)}
                    </Typography>
                  </div>

                  <div className={classes.dataColumn}>
                    <Typography className={classes.dataLabel}>Contexto</Typography>
                    <Typography variant="body2">
                      {task.assignedUser?.name || "Sin asignar"}
                    </Typography>
                    {task.contact ? (
                      <Typography variant="caption" color="textSecondary">
                        {`${task.contact.name}${task.contact.number ? ` · ${task.contact.number}` : ""}`}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Sin contacto referenciado
                      </Typography>
                    )}
                  </div>

                  <div className={classes.actionsRow}>
                    <Button size="small" variant="outlined" color="primary" startIcon={<HistoryIcon />} onClick={() => loadHistory(task)}>
                      Historial
                    </Button>
                    <Button size="small" variant="outlined" color="primary" startIcon={<EditIcon />} onClick={() => openEditDialog(task)}>
                      Editar
                    </Button>
                    <Button size="small" variant="outlined" color="primary" startIcon={<FileCopyIcon />} onClick={() => openDuplicateDialog(task)}>
                      Duplicar
                    </Button>
                    {task.status === "pending" && (
                      <Button size="small" variant="contained" color="primary" startIcon={<PlayArrowIcon />} onClick={() => handleTransition(task, "start")}>
                        Iniciar
                      </Button>
                    )}
                    {task.status === "in_progress" && (
                      <>
                        <Button size="small" variant="outlined" color="primary" startIcon={<PauseIcon />} onClick={() => openPauseDialog(task)}>
                          Pausar
                        </Button>
                        <Button size="small" variant="contained" style={{ backgroundColor: "#0f9d58", color: "#fff" }} startIcon={<CheckCircleIcon />} onClick={() => handleTransition(task, "complete")}>
                          Terminar
                        </Button>
                      </>
                    )}
                    {task.status === "paused" && (
                      <>
                        <Button size="small" variant="contained" color="primary" startIcon={<ReplayIcon />} onClick={() => handleTransition(task, "resume")}>
                          Retomar
                        </Button>
                        <Button size="small" variant="outlined" style={{ color: "#0f9d58", borderColor: alpha("#0f9d58", 0.4) }} startIcon={<CheckCircleIcon />} onClick={() => handleTransition(task, "complete")}>
                          Cerrar
                        </Button>
                      </>
                    )}
                    {task.status === "completed" && (
                      <Button size="small" variant="outlined" color="primary" startIcon={<ReplayIcon />} onClick={() => openReopenDialog(task)}>
                        Reabrir
                      </Button>
                    )}
                  </div>
                </div>

                <div style={{ display: "none" }}>
                  <Typography variant="caption" color="textSecondary">
                    Inicio: {task.startedAt ? formatDateTime(task.startedAt) : "Aún no inicia"} | Último movimiento: {formatDateTime(task.updatedAt)}
                  </Typography>
                </div>
              </div>
            );
          })}

          <div className={classes.paginationWrap}>
            <TablePagination
              component="div"
              count={tasksToRender.length}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={event => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 8, 12, 20]}
              labelRowsPerPage="Registros por página"
            />
          </div>
          </Paper>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingTask ? "Editar tarea operativa" : "Nueva tarea operativa"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth variant="outlined" label="Título de la tarea" value={form.title} onChange={event => setForm(prev => ({ ...prev, title: event.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth variant="outlined" label="Prioridad" value={form.priority} onChange={event => setForm(prev => ({ ...prev, priority: event.target.value }))}>
                {PRIORITY_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Responsable"
                value={canSeeAll ? form.assignedUserId : String(user?.id || "")}
                onChange={event => setForm(prev => ({ ...prev, assignedUserId: event.target.value }))}
                disabled={!canSeeAll}
              >
                {(canSeeAll ? users : [user]).map(option => (
                  <MenuItem key={option.id} value={String(option.id)}>{option.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={clientOptions}
                value={selectedClient}
                onChange={(_event, value) => {
                  setForm(prev => ({
                    ...prev,
                    businessCustomerId: value ? String(value.id) : "",
                    customerEstablishmentId: ""
                  }));
                }}
                getOptionLabel={option =>
                  `${option.name || "Cliente sin nombre"}${option.identificationNumber ? ` - ${option.identificationNumber}` : ""}`
                }
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                noOptionsText="No hay clientes disponibles"
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Cliente relacionado (opcional)"
                    placeholder="Buscar cliente por nombre o documento"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={establishments}
                value={selectedEstablishment}
                onChange={(_event, value) => {
                  setForm(prev => ({
                    ...prev,
                    customerEstablishmentId: value ? String(value.id) : ""
                  }));
                }}
                getOptionLabel={option =>
                  `${option.name || "Sucursal sin nombre"}${option.municipalityName ? ` - ${option.municipalityName}` : ""}`
                }
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                noOptionsText={form.businessCustomerId ? "No hay sucursales disponibles" : "Seleccione primero un cliente"}
                disabled={!form.businessCustomerId}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Sucursal relacionada (opcional)"
                    placeholder="Buscar sucursal"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={taskContactOptions}
                value={selectedTaskContact}
                onChange={(_event, value) => {
                  setForm(prev => ({ ...prev, contactId: value ? String(value.id) : "" }));
                }}
                getOptionLabel={option => buildTaskContactLabel(option)}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                noOptionsText="No hay contactos disponibles"
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Contacto relacionado (opcional)"
                    placeholder="Buscar por nombre o número"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={4} variant="outlined" label="Descripción" value={form.description} onChange={event => setForm(prev => ({ ...prev, description: event.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleSave}>
            {editingTask ? "Guardar cambios" : "Crear tarea"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pauseDialog.open} onClose={() => setPauseDialog({ open: false, task: null })} fullWidth maxWidth="sm">
        <DialogTitle>Pausar tarea</DialogTitle>
        <DialogContent dividers>
          <TextField
            select
            fullWidth
            variant="outlined"
            label="Motivo de pausa"
            value={actionForm.reason}
            onChange={event => setActionForm(prev => ({ ...prev, reason: event.target.value }))}
            style={{ marginBottom: 16 }}
          >
            {PAUSE_REASON_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            label="Observación adicional (opcional)"
            value={actionForm.note}
            onChange={event => setActionForm(prev => ({ ...prev, note: event.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseDialog({ open: false, task: null })}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!actionForm.reason) {
                toast.warning("Seleccione un motivo de pausa.");
                return;
              }
              await handleTransition(pauseDialog.task, "pause", actionForm);
              setPauseDialog({ open: false, task: null });
              setActionForm(emptyActionForm);
            }}
          >
            Guardar pausa
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reopenDialog.open} onClose={() => setReopenDialog({ open: false, task: null })} fullWidth maxWidth="sm">
        <DialogTitle>Reabrir tarea</DialogTitle>
        <DialogContent dividers>
          <TextField
            select
            fullWidth
            variant="outlined"
            label="Justificación de reapertura"
            value={actionForm.reason}
            onChange={event => setActionForm(prev => ({ ...prev, reason: event.target.value }))}
            style={{ marginBottom: 16 }}
          >
            {REOPEN_REASON_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            label="Detalle de reapertura"
            value={actionForm.note}
            onChange={event => setActionForm(prev => ({ ...prev, note: event.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenDialog({ open: false, task: null })}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!actionForm.reason) {
                toast.warning("Seleccione la justificación de reapertura.");
                return;
              }
              await handleTransition(reopenDialog.task, "reopen", actionForm);
              setReopenDialog({ open: false, task: null });
              setActionForm(emptyActionForm);
            }}
          >
            Reabrir tarea
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyDialog.open} onClose={() => setHistoryDialog({ open: false, task: null, events: [] })} fullWidth maxWidth="md">
        <DialogTitle>
          Historial de tarea
          {historyDialog.task ? `: ${historyDialog.task.title}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {historyDialog.events.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Esta tarea todavía no tiene historial visible.
            </Typography>
          ) : (
            historyDialog.events.map(event => (
              <Paper key={event.id} className={classes.historyItem} elevation={0}>
                <Typography variant="subtitle2" style={{ fontWeight: 800 }}>
                  {getEventLabel(event.eventType)} | {event.user?.name || "Usuario"} | {formatDateTime(event.createdAt)}
                </Typography>
                {event.reason && (
                  <Typography variant="body2" color="textSecondary">
                    Motivo: {event.reason}
                  </Typography>
                )}
                {event.note && (
                  <Typography variant="body2" style={{ marginTop: 6 }}>
                    {event.note}
                  </Typography>
                )}
                <Typography variant="caption" color="textSecondary">
                  Tiempo acumulado a ese momento: {formatDuration(event.elapsedSecondsSnapshot)}
                </Typography>
              </Paper>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog({ open: false, task: null, events: [] })}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
}
